import { useQuery } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import session from 'lib/api/links/session';

import { GET_RECENT_ACTIVITY_SETS } from 'lib/api/queries';
import { getMachine } from 'lib/selectors/getMachine';
import {
  actionSetRecentActivitySets,
  actionSetCurrentWorkOrderOp,
  actionSetWorkOrderOperationLaborTickets,
  actionSetWorkOrderOperationParts,
} from 'lib/actions';
import { useMachineOperationalStatus } from 'lib/hooks/useMachineOperationalStatus';
import { getPostLaborUpdatesOnNats } from 'lib/selectors/getPostLaborUpdatesOnNats';
import { getIsCompanyLoading } from 'lib/selectors/getCompany';
import { getIsApmERPJobFlow } from 'lib/selectors/getIsApmERPJobFlow';

export const useRequestRecentActivitySets = () => {
  const machine = useSelector(getMachine);
  const dispatch = useDispatch();
  const natsFlowEnabled = useSelector(getPostLaborUpdatesOnNats);
  const isCompanyLoading = useSelector(getIsCompanyLoading);
  const isApmERPJobFlow = useSelector(getIsApmERPJobFlow);

  const fetchMachineOperationalStatus = useMachineOperationalStatus();

  // Recent Activity Sets - skip in NATS mode and APM ERP mode (labor ticket / program event is source of truth)
  // Also skip while company settings are loading to avoid premature query
  const requestRecentActivitySets = useQuery(GET_RECENT_ACTIVITY_SETS, {
    variables: {
      machineRef: machine.machineRef,
      includeJobData: true,
      // limiting to 1 for R1, but will support multiple open sets in future
      limit: 1,
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip:
      !machine.machineRef ||
      isCompanyLoading ||
      natsFlowEnabled ||
      isApmERPJobFlow,
    onCompleted: ({ activitySets }) => {
      if (!activitySets) {
        /*
the main intent here is to handle case where user switches companies
and the req for the old machine is now out of company scope; we clear the
token tied to the old company and redirect the user to the machine switcher so
they can pull a new token for the company they are now switched to
*/
        session.clear();
        window.location = '/switch-machines';
        return;
      }
      if (activitySets?.activitySets?.[0]?.closedAt) {
        dispatch(actionSetCurrentWorkOrderOp(null));
        dispatch(actionSetWorkOrderOperationLaborTickets(null));
        dispatch(actionSetWorkOrderOperationParts(null));
      }
      dispatch(actionSetRecentActivitySets(activitySets));
      fetchMachineOperationalStatus();
    },
  });

  return requestRecentActivitySets;
};
